import React, { useMemo, useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderOpen, ClipboardPaste, Download, Copy, RotateCcw, X, Wrench, ListTree,
  Search, Filter, ShieldCheck, History, FileCode, Undo2, PanelLeft,
  CheckSquare, Square, Files, Folders, GitCompareArrows,
  Play, Cpu, MoreVertical, CheckCircle2, Loader2,
} from "lucide-react";
import ChatAssistant from "@/components/ChatAssistant";
import RenameTable from "@/components/RenameTable";
import ManualPanel from "@/components/ManualPanel";
import RecipeTimeline from "@/components/RecipeTimeline";
import {
  pickDirectory, parsePastedList, supportsDirectoryPicker,
  pickFiles, supportsFilePicker, readDropItems,
} from "@/lib/fs";
import { replay, uid } from "@/lib/rename/recipe";
import { detectCollisions, resolveConflicts } from "@/lib/rename/collisions";
import { buildRenameScript, buildUndoScript, downloadText } from "@/lib/rename/script";
import { suggestRenames, chatWithAssistant } from "@/lib/api";
import { getSessions, saveSession } from "@/lib/history";
import { getAdapter, resolveEdition } from "@/lib/adapters";
import { hasNative, nativeChooseFolder } from "@/lib/adapters/nativeBridge";

const WALLPAPER =
  "https://images.unsplash.com/photo-1658660854207-8886b1d69bb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBob2xvZ3JhcGhpY3xlbnwwfHx8fDE3ODQxOTAwMTZ8MA&ixlib=rb-4.1.0&q=85";

const PRESET_LABELS = {
  extract_names: "AI: Extract names",
  clean_underscores: "AI: Clean separators",
  remove_tags: "AI: Remove tags",
  title_case: "AI: Title Case",
  snake_case: "AI: snake_case",
  kebab_case: "AI: kebab-case",
};

const RECIPES_KEY = "rs_recipes_v1";
const loadSavedRecipes = () => {
  try { return JSON.parse(localStorage.getItem(RECIPES_KEY) || "[]"); } catch { return []; }
};

function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="flex bg-black/40 border border-white/10 rounded-full p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${testid}-${o.key}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${value === o.key ? "bg-[#00E5FF] text-black" : "text-neutral-300 hover:text-white"}`}>
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}

// Rebuild item names/paths after a committed rename (apply or undo), honoring
// renamed parent folders so descendant paths stay correct. changedMap: id->newBasename.
function commitItems(items, changedMap) {
  const sorted = [...items].sort((a, b) => (a.depth || 0) - (b.depth || 0));
  const finalByOldPath = {};
  const parentById = {};
  for (const it of sorted) {
    const parentFinal = it.parent ? (finalByOldPath[it.parent] || it.parent) : "";
    const newBase = changedMap[it.id] != null ? changedMap[it.id] : it.name;
    finalByOldPath[it.path] = parentFinal ? `${parentFinal}/${newBase}` : newBase;
    parentById[it.id] = parentFinal;
  }
  return items.map((it) => ({
    ...it,
    name: changedMap[it.id] != null ? changedMap[it.id] : it.name,
    path: finalByOldPath[it.path],
    parent: parentById[it.id],
  }));
}

export default function RenameStudio() {
  const [items, setItems] = useState([]);
  const [dirName, setDirName] = useState("");
  const [recipe, setRecipe] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [excluded, setExcluded] = useState(new Set());
  const [reasonsById, setReasonsById] = useState({});
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiScope, setAiScope] = useState("all");
  const [panel, setPanel] = useState("manual");
  const [view, setView] = useState("table");
  const [filterKind, setFilterKind] = useState("all");
  const [filterExt, setFilterExt] = useState("");
  const [filterDepth, setFilterDepth] = useState("");
  const [search, setSearch] = useState("");
  const [conflictStyle, setConflictStyle] = useState("space");
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [showScript, setShowScript] = useState(false);
  const [scriptTab, setScriptTab] = useState("rename");
  const [showHistory, setShowHistory] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [savedRecipes, setSavedRecipes] = useState(loadSavedRecipes());
  const [sessions, setSessions] = useState(getSessions());
  const [edition, setEdition] = useState(resolveEdition());
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(null); // {done,total} while renaming
  const [completion, setCompletion] = useState(null); // {files,folders} after apply
  const [lastApplied, setLastApplied] = useState(null); // completed steps for undo
  const [showAdvanced, setShowAdvanced] = useState(false);
  const sessionId = useRef(uid()).current;

  const adapter = useMemo(() => getAdapter(edition), [edition]);
  const switchEdition = (ed) => {
    setEdition(ed); localStorage.setItem("rs_edition", ed);
    setCompletion(null); setLastApplied(null);
  };

  // ---- derived: proposed names via recipe replay + manual overrides ----
  const proposedMap = useMemo(() => {
    const map = replay(items, recipe);
    Object.entries(overrides).forEach(([id, name]) => { if (map.has(id)) map.set(id, name); });
    return map;
  }, [items, recipe, overrides]);

  const baseRows = useMemo(
    () =>
      items.map((it) => ({
        id: it.id, original: it.name, suggested: proposedMap.get(it.id) ?? it.name,
        path: it.path, parent: it.parent, type: it.type, depth: it.depth, ext: it.ext,
        include: !excluded.has(it.id), edited: overrides[it.id] != null,
        reason: reasonsById[it.id] || "", _handle: it._handle, rootId: it.rootId,
      })),
    [items, proposedMap, excluded, overrides, reasonsById]
  );

  const collision = useMemo(() => detectCollisions(baseRows), [baseRows]);
  const rows = useMemo(
    () => baseRows.map((r) => ({ ...r, status: collision.statusById[r.id] })),
    [baseRows, collision]
  );

  const exts = useMemo(
    () => [...new Set(items.filter((i) => i.type === "file" && i.ext).map((i) => i.ext))].sort(),
    [items]
  );
  const maxDepth = useMemo(() => items.reduce((m, i) => Math.max(m, i.depth || 0), 0), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterKind === "files" && r.type !== "file") return false;
      if (filterKind === "folders" && r.type !== "folder") return false;
      if (filterKind === "changed" && r.suggested === r.original) return false;
      if (filterKind === "unchanged" && r.suggested !== r.original) return false;
      if (filterKind === "conflicts" && !(r.status === "conflict" || r.status === "case")) return false;
      if (filterExt && r.ext !== filterExt) return false;
      if (filterDepth !== "" && r.depth !== Number(filterDepth)) return false;
      if (q && !(r.original.toLowerCase().includes(q) || r.suggested.toLowerCase().includes(q) || (r.path || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, filterKind, filterExt, filterDepth, search]);

  const summary = useMemo(() => {
    const changed = rows.filter((r) => r.include && r.suggested !== r.original);
    return {
      total: rows.length,
      files: changed.filter((r) => r.type === "file").length,
      folders: changed.filter((r) => r.type === "folder").length,
      unchanged: rows.length - changed.length,
      conflicts: collision.count,
    };
  }, [rows, collision]);

  // ---- import ----
  // Additive import — never wipes the existing session (multiple folders/files).
  const addItems = (newItems, name = "") => {
    if (!newItems?.length) return;
    setDirName((d) => d || name);
    setItems((prev) => {
      const seen = new Set(prev.map((i) => `${i.rootId || ""}|${i.path}`));
      const merged = prev.slice();
      newItems.forEach((it) => { const k = `${it.rootId || ""}|${it.path}`; if (!seen.has(k)) { seen.add(k); merged.push(it); } });
      return merged;
    });
    setCompletion(null); setLastApplied(null);
  };
  const clearAll = () => {
    setItems([]); setRecipe([]); setOverrides({}); setExcluded(new Set());
    setReasonsById({}); setDirName(""); setCompletion(null); setLastApplied(null);
    toast.success("Cleared session");
  };

  const openFolder = async () => {
    // Native Mac app: use the OS folder chooser with user-selected read/write.
    if (edition === "mac" && hasNative()) {
      try {
        const res = await nativeChooseFolder();
        if (res) {
          addItems(res.items, res.dirName);
          toast.success(`Imported ${res.items.length} item(s) from "${res.dirName}"`);
          if (res.truncated) toast.warning("Large tree truncated to 2000 items.");
        }
      } catch (e) { toast.error("Could not open the folder."); }
      return;
    }
    if (!supportsDirectoryPicker()) { toast.error("Folder access needs Chrome or Edge. Use Paste instead."); setShowPaste(true); return; }
    try {
      const { dirName, items, truncated } = await pickDirectory();
      addItems(items, dirName);
      toast.success(`Imported ${items.length} item(s) from "${dirName}"`);
      if (truncated) toast.warning("Large tree truncated to 2000 items.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not open the folder."); }
  };

  const addFilesHandler = async () => {
    if (!supportsFilePicker()) { toast.error("Adding individual files needs Chrome or Edge."); return; }
    try {
      const files = await pickFiles();
      addItems(files, files[0]?.name || "");
      toast.success(`Added ${files.length} file(s)`);
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not add files."); }
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    try {
      const dropped = await readDropItems(e.dataTransfer);
      if (!dropped.length) { toast.error("Drop files or folders (Chrome/Edge)."); return; }
      addItems(dropped, dropped.find((d) => d.isRoot)?.name || "");
      toast.success(`Added ${dropped.length} item(s)`);
    } catch { toast.error("Could not read the dropped items."); }
  };

  const applyPaste = () => {
    const its = parsePastedList(pasteText);
    if (!its.length) { toast.error("Paste at least one name."); return; }
    const rootId = uid();
    its.forEach((i) => { i.rootId = rootId; });
    addItems(its, its[0]?.path?.split("/")[0] || "");
    setShowPaste(false); setPasteText("");
    toast.success(`Loaded ${its.length} name(s)`);
  };

  // ---- recipe steps ----
  const includedIds = () => rows.filter((r) => r.include).map((r) => r.id);
  const addStep = (step) => {
    const s = { ...step, id: uid() };
    if (s.scope === "selected") s.ids = includedIds();
    setRecipe((prev) => [...prev, s]);
    toast.success("Step added");
  };
  const removeStep = (id) => setRecipe((prev) => prev.filter((s) => s.id !== id));
  const moveStep = (i, dir) => setRecipe((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const next = prev.slice(); [next[i], next[j]] = [next[j], next[i]]; return next;
  });

  // ---- AI ----
  const scopedForAI = () => {
    let base = rows;
    if (aiScope === "selected") base = rows.filter((r) => r.include);
    else if (aiScope === "files") base = rows.filter((r) => r.type === "file");
    else if (aiScope === "folders") base = rows.filter((r) => r.type === "folder");
    return base.map((r) => ({ id: r.id, name: r.suggested, type: r.type, path: r.path, parent: r.parent, depth: r.depth, ext: r.ext }));
  };
  const bakeAiStep = (suggestions, label) => {
    const map = {}, reasons = {};
    suggestions.forEach((s) => { map[s.id] = s.suggested; if (s.reason) reasons[s.id] = s.reason; });
    setRecipe((prev) => [...prev, { id: uid(), type: "ai", label, map }]);
    setReasonsById((prev) => ({ ...prev, ...reasons }));
  };

  const runPreset = async (preset) => {
    const scoped = scopedForAI();
    if (!scoped.length) { toast.error("Nothing in the selected AI scope."); return; }
    setBusy(true);
    try {
      const { suggestions } = await suggestRenames({ items: scoped, preset });
      bakeAiStep(suggestions, PRESET_LABELS[preset] || "AI rename");
      toast.success("Preview updated");
    } catch (e) {
      toast.error(e?.response?.status === 413 ? "Too many items — narrow the AI scope." : "AI request failed.");
    } finally { setBusy(false); }
  };

  const handleChat = async (text) => {
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { reply, suggestions } = await chatWithAssistant({ sessionId, message: text, items: scopedForAI() });
      if (suggestions?.length) bakeAiStep(suggestions, `AI: ${text.slice(0, 40)}`);
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Something went wrong reaching the AI. Please try again." }]);
    } finally { setBusy(false); }
  };

  // ---- selection & edits ----
  const toggleRow = (id) => setExcluded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllFiltered = () => {
    const allIncluded = filtered.every((r) => r.include);
    setExcluded((prev) => { const n = new Set(prev); filtered.forEach((r) => (allIncluded ? n.add(r.id) : n.delete(r.id))); return n; });
  };
  const selectPredicate = (pred) => setExcluded(new Set(rows.filter((r) => !pred(r)).map((r) => r.id)));
  const editRow = (id, value) => setOverrides((prev) => ({ ...prev, [id]: value }));
  const resetAll = () => { setRecipe([]); setOverrides({}); setReasonsById({}); toast.success("Reset all changes"); };
  const resetSelected = () => { setOverrides((prev) => { const n = { ...prev }; rows.filter((r) => r.include).forEach((r) => { delete n[r.id]; }); return n; }); toast.success("Cleared manual edits on selected"); };

  const fixConflicts = () => {
    const ov = resolveConflicts(rows, conflictStyle);
    if (!Object.keys(ov).length) { toast.info("No conflicts to fix."); return; }
    setOverrides((prev) => ({ ...prev, ...ov }));
    toast.success(`Resolved ${Object.keys(ov).length} conflict(s)`);
  };

  // ---- recipes ----
  const saveRecipe = () => {
    const deterministic = recipe.filter((s) => s.type !== "ai");
    if (!deterministic.length) { toast.error("No manual steps to save."); return; }
    const name = window.prompt("Recipe name", "My Recipe");
    if (!name) return;
    const next = [...savedRecipes, { id: uid(), name, steps: deterministic }];
    setSavedRecipes(next); localStorage.setItem(RECIPES_KEY, JSON.stringify(next));
    toast.success("Recipe saved");
  };
  const loadRecipe = (r) => { setRecipe((prev) => [...prev, ...r.steps.map((s) => ({ ...s, id: uid() }))]); toast.success(`Loaded "${r.name}"`); };

  // ---- export ----
  const changedCount = summary.files + summary.folders;
  const renameScript = useMemo(() => buildRenameScript(rows, dirName), [rows, dirName]);
  const undoScript = useMemo(() => buildUndoScript(rows, dirName), [rows, dirName]);

  const guardConflicts = () => {
    if (collision.hasConflicts) { toast.error("Resolve conflicts before exporting."); setFilterKind("conflicts"); return false; }
    if (!changedCount) { toast.error("Nothing to rename yet."); return false; }
    return true;
  };
  const exportScripts = () => {
    if (!guardConflicts()) return;
    downloadText("rename.sh", renameScript);
    downloadText("undo_rename.sh", undoScript);
    const next = saveSession({ folder: dirName || "(pasted)", files: summary.files, folders: summary.folders, renamed: changedCount, mode: recipe.some((s) => s.type === "ai") ? "AI + Manual" : "Manual", undo: true });
    setSessions(next);
    toast.success("Exported rename.sh + undo_rename.sh");
  };
  const copyCommands = async () => {
    if (!guardConflicts()) return;
    await navigator.clipboard.writeText(renameScript);
    toast.success("Rename commands copied");
  };

  // ---- Mac Edition: direct apply + undo ----
  const requestApply = () => {
    if (collision.hasConflicts) { toast.error("Resolve conflicts before applying."); setFilterKind("conflicts"); return; }
    if (!changedCount) return;
    setShowConfirm(true);
  };
  const confirmApply = async () => {
    setShowConfirm(false);
    setApplying({ done: 0, total: changedCount });
    const res = await adapter.applyPlan(rows, { onProgress: (done, total) => setApplying({ done, total }) });
    setApplying(null);
    if (!res.ok) {
      toast.error(`${res.error}${res.rolledBack ? " Changes were rolled back." : ""}`);
      return;
    }
    const changedMap = {}; res.completed.forEach((s) => { changedMap[s.id] = s.newBasename; });
    setLastApplied(res.completed);
    setCompletion({ files: summary.files, folders: summary.folders });
    setItems((prev) => commitItems(prev, changedMap));
    setRecipe([]); setOverrides({}); setExcluded(new Set()); setReasonsById({});
    const next = saveSession({ folder: dirName || "(folder)", files: summary.files, folders: summary.folders, renamed: res.applied, mode: recipe.some((s) => s.type === "ai") ? "AI + Manual" : "Manual", undo: true, applied: true });
    setSessions(next);
    toast.success(`Renamed ${res.applied} item(s)`);
  };
  const undoApply = async () => {
    if (!lastApplied?.length) return;
    setApplying({ done: 0, total: lastApplied.length });
    try {
      await adapter.undoPlan(lastApplied);
      const revertMap = {}; lastApplied.forEach((s) => { revertMap[s.id] = s.oldBasename; });
      setItems((prev) => commitItems(prev, revertMap));
      setCompletion(null); setLastApplied(null);
      toast.success("Undo complete — names restored");
    } catch (e) {
      toast.error(`Undo failed: ${e.message}`);
    } finally { setApplying(null); }
  };

  const persistedEdition = useRef(false);
  useEffect(() => {
    if (!persistedEdition.current) { persistedEdition.current = true; localStorage.setItem("rs_edition", edition); }
  }, [edition]);

  return (
    <div className="h-screen w-screen flex items-center justify-center overflow-hidden p-3 sm:p-6 bg-cover bg-center" style={{ backgroundImage: `url(${WALLPAPER})` }}>
      <div className="h-[92vh] w-full max-w-[1500px] rounded-2xl overflow-hidden flex shadow-2xl ring-1 ring-white/20 bg-[#0A0A0A]/80 backdrop-blur-3xl">
        {/* Sidebar (desktop) */}
        <div className="w-[330px] flex-shrink-0 hidden lg:block">
          <ChatAssistant messages={messages} onSend={handleChat} onPreset={runPreset} busy={busy} hasItems={items.length > 0} scope={aiScope} setScope={setAiScope} />
        </div>

        {/* Chat drawer (mobile/tablet) */}
        <AnimatePresence>
          {showChat && (
            <motion.div className="fixed inset-0 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="absolute inset-0 bg-black/60" onClick={() => setShowChat(false)} />
              <motion.div className="absolute left-0 top-0 h-full w-[85vw] max-w-sm" initial={{ x: -400 }} animate={{ x: 0 }} exit={{ x: -400 }} transition={{ type: "tween" }}>
                <ChatAssistant messages={messages} onSend={handleChat} onPreset={runPreset} busy={busy} hasItems={items.length > 0} scope={aiScope} setScope={setAiScope} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0 bg-[#0A0A0A]/90 relative">
          {/* Title bar */}
          <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10 bg-[#0A0A0A]">
            <div className="hidden sm:flex gap-2"><span className="h-3 w-3 rounded-full bg-[#FF5F57]" /><span className="h-3 w-3 rounded-full bg-[#FEBC2E]" /><span className="h-3 w-3 rounded-full bg-[#28C840]" /></div>
            <button className="lg:hidden text-neutral-400 hover:text-white" onClick={() => setShowChat(true)} data-testid="open-chat-drawer"><PanelLeft className="h-5 w-5" /></button>
            <div className="ml-1 flex items-baseline gap-2 min-w-0">
              <h1 className="font-display font-black text-white text-base sm:text-lg tracking-tight truncate">Rename Studio AI</h1>
              {dirName && <span className="font-mono-ui text-xs text-neutral-500 truncate">~/{dirName}</span>}
            </div>
            <button onClick={() => { setSessions(getSessions()); setShowHistory(true); }} data-testid="history-btn" className="ml-auto flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white transition-colors duration-200"><History className="h-4 w-4" /> History</button>
            <div className="flex bg-black/40 border border-white/10 rounded-full p-0.5" data-testid="edition-toggle">
              {[["web", "Web"], ["mac", "Mac"]].map(([k, l]) => (
                <button key={k} onClick={() => switchEdition(k)} data-testid={`edition-${k}`} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors duration-200 ${edition === k ? "bg-[#00E5FF] text-black" : "text-neutral-300 hover:text-white"}`}>{k === "mac" ? <Cpu className="h-3 w-3" /> : null}{l}</button>
              ))}
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-[#0A0A0A]/95 flex-wrap">
            <button onClick={openFolder} data-testid="open-folder-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-[#00E5FF] text-black hover:bg-[#00B3CC] hover:scale-105 transition-all duration-200"><FolderOpen className="h-4 w-4" /> {items.length ? "+ Add Folder" : adapter.importVerb}</button>
            <button onClick={addFilesHandler} data-testid="add-files-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200"><Files className="h-4 w-4" /> {items.length ? "+ Add Files" : "Add Files"}</button>
            <button onClick={() => setShowPaste(true)} data-testid="paste-names-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200"><ClipboardPaste className="h-4 w-4" /> Paste Names</button>
            {items.length > 0 && <button onClick={clearAll} data-testid="clear-all-btn" className="flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium bg-white/5 text-neutral-300 border border-white/15 hover:bg-white/10 transition-all duration-200"><RotateCcw className="h-4 w-4" /> Clear All</button>}
            <div className="h-6 w-px bg-white/10 mx-1" />
            <Segmented value={panel} onChange={setPanel} testid="panel-tabs" options={[{ key: "manual", label: "Manual Tools", icon: <Wrench className="h-3.5 w-3.5" /> }, { key: "timeline", label: "Recipe", icon: <ListTree className="h-3.5 w-3.5" /> }]} />
            <Segmented value={view} onChange={setView} testid="view-tabs" options={[{ key: "table", label: "Table", icon: <Filter className="h-3.5 w-3.5" /> }, { key: "tree", label: "Before / After", icon: <GitCompareArrows className="h-3.5 w-3.5" /> }]} />
          </div>

          {items.length === 0 ? (
            <div
              className={`flex-1 flex flex-col items-center justify-center text-center px-8 m-4 rounded-2xl border-2 border-dashed transition-colors duration-200 ${dragOver ? "border-[#00E5FF] bg-[#00E5FF]/5" : "border-white/10"}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="dropzone"
            >
              <div className="h-16 w-16 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/25 flex items-center justify-center mb-6"><FolderOpen className="h-7 w-7 text-[#00E5FF] ai-glow" /></div>
              <h2 className="font-display font-black text-2xl text-white mb-2">Import to begin</h2>
              <p className="text-neutral-400 max-w-md leading-relaxed">
                Open a folder to rename everything inside it (files and subfolders, any depth), or add individual files. Any file type is supported — the greyed-out files in the folder chooser still load. Drag & drop works too.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                <button onClick={openFolder} data-testid="empty-open-folder" className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#00E5FF] text-black hover:bg-[#00B3CC] transition-all duration-200"><FolderOpen className="h-4 w-4" /> {adapter.importVerb}</button>
                <button onClick={addFilesHandler} data-testid="empty-add-files" className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200"><Files className="h-4 w-4" /> Add Files</button>
              </div>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="flex items-center gap-4 px-4 py-2.5 border-b border-white/10 bg-[#0A0A0A]/95 text-sm font-mono-ui flex-wrap" data-testid="summary-bar">
                <span className="text-neutral-400"><b className="text-white">{summary.total}</b> items</span>
                <span className="text-neutral-400"><b className="text-[#34C759]">{summary.files}</b> files</span>
                <span className="text-neutral-400"><b className="text-[#34C759]">{summary.folders}</b> folders renamed</span>
                <span className="text-neutral-400"><b className="text-neutral-300">{summary.unchanged}</b> unchanged</span>
                <span className={summary.conflicts ? "text-[#FF3B30]" : "text-neutral-600"}><b>{summary.conflicts}</b> conflicts</span>
                {summary.conflicts > 0 && (
                  <div className="flex items-center gap-2 ml-auto">
                    <select value={conflictStyle} onChange={(e) => setConflictStyle(e.target.value)} data-testid="conflict-style" className="bg-black/60 border border-white/15 rounded-lg px-2 py-1 text-xs text-white outline-none">
                      <option value="space">Name 2</option>
                      <option value="paren">Name (1)</option>
                    </select>
                    <button onClick={fixConflicts} data-testid="fix-conflicts-btn" className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-[#FEBC2E] text-black hover:brightness-110 transition-all duration-200"><ShieldCheck className="h-3.5 w-3.5" /> Fix Conflicts</button>
                  </div>
                )}
              </div>

              {/* Filters + search */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-[#0A0A0A]/90 flex-wrap text-xs">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {["all", "files", "folders", "changed", "unchanged", "conflicts"].map((k) => (
                    <button key={k} onClick={() => setFilterKind(k)} data-testid={`filter-${k}`} className={`px-2.5 py-1 rounded-full border transition-colors duration-200 ${filterKind === k ? "bg-white/15 text-white border-white/25" : "border-white/10 text-neutral-400 hover:text-white"}`}>{k}</button>
                  ))}
                </div>
                {exts.length > 0 && (
                  <select value={filterExt} onChange={(e) => setFilterExt(e.target.value)} data-testid="filter-ext" className="bg-black/60 border border-white/15 rounded-lg px-2 py-1 text-white outline-none">
                    <option value="">any ext</option>
                    {exts.map((e) => <option key={e} value={e}>{e}</option>)}
                  </select>
                )}
                <select value={filterDepth} onChange={(e) => setFilterDepth(e.target.value)} data-testid="filter-depth" className="bg-black/60 border border-white/15 rounded-lg px-2 py-1 text-white outline-none">
                  <option value="">any depth</option>
                  {Array.from({ length: maxDepth + 1 }).map((_, d) => <option key={d} value={d}>{`depth ${d}`}</option>)}
                </select>
                <div className="flex items-center gap-1.5 bg-black/60 border border-white/15 rounded-lg px-2 py-1 ml-auto">
                  <Search className="h-3.5 w-3.5 text-neutral-500" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="search names / paths" data-testid="search-input" className="bg-transparent outline-none text-white placeholder:text-neutral-600 w-44" />
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => selectPredicate(() => true)} data-testid="select-all-items" className="p-1 text-neutral-400 hover:text-white" title="Select all"><CheckSquare className="h-4 w-4" /></button>
                  <button onClick={() => selectPredicate(() => false)} data-testid="select-none-items" className="p-1 text-neutral-400 hover:text-white" title="Select none"><Square className="h-4 w-4" /></button>
                  <button onClick={() => selectPredicate((r) => r.type === "file")} data-testid="select-files" className="p-1 text-neutral-400 hover:text-white" title="Files only"><Files className="h-4 w-4" /></button>
                  <button onClick={() => selectPredicate((r) => r.type === "folder")} data-testid="select-folders" className="p-1 text-neutral-400 hover:text-white" title="Folders only"><Folders className="h-4 w-4" /></button>
                </div>
              </div>

              {/* Body: side panel + table/tree */}
              <div className="flex flex-1 min-h-0">
                <div className="w-[300px] flex-shrink-0 border-r border-white/10 hidden md:block">
                  {panel === "manual"
                    ? <ManualPanel onAddStep={addStep} hasItems={items.length > 0} />
                    : <RecipeTimeline recipe={recipe} onRemove={removeStep} onMove={moveStep} onSaveRecipe={saveRecipe} savedRecipes={savedRecipes} onLoadRecipe={loadRecipe} />}
                </div>

                {view === "table" ? (
                  <RenameTable rows={filtered} onToggle={toggleRow} onEdit={editRow} onToggleAll={toggleAllFiltered} />
                ) : (
                  <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-0" data-testid="before-after">
                    <div className="border-r border-white/10">
                      <div className="sticky top-0 bg-[#0A0A0A] px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/10">Current</div>
                      {filtered.map((r) => (
                        <div key={r.id} className="px-4 py-1.5 text-sm font-mono-ui text-neutral-400 truncate" style={{ paddingLeft: `${16 + Math.min(r.depth || 0, 8) * 14}px` }}>{r.original}</div>
                      ))}
                    </div>
                    <div>
                      <div className="sticky top-0 bg-[#0A0A0A] px-4 py-2 text-[10px] uppercase tracking-widest text-[#00E5FF] border-b border-white/10">After rename</div>
                      {filtered.map((r) => (
                        <div key={r.id} className={`px-4 py-1.5 text-sm font-mono-ui truncate ${r.suggested !== r.original ? "text-[#34C759]" : "text-neutral-400"}`} style={{ paddingLeft: `${16 + Math.min(r.depth || 0, 8) * 14}px` }}>{r.include ? r.suggested : r.original}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-white/10 bg-[#0A0A0A] flex-wrap">
                <div className="text-sm font-mono-ui text-neutral-400">{changedCount} of {summary.total} will change</div>

                {edition === "web" ? (
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <button onClick={resetSelected} data-testid="reset-selected-btn" className="rounded-full px-3 py-2 text-xs bg-white/5 text-neutral-300 border border-white/15 hover:bg-white/10 transition-all duration-200">Reset selected</button>
                    <button onClick={resetAll} data-testid="reset-btn" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs bg-white/5 text-neutral-300 border border-white/15 hover:bg-white/10 transition-all duration-200"><RotateCcw className="h-3.5 w-3.5" /> Reset all</button>
                    <button onClick={() => setShowScript(true)} disabled={!changedCount} data-testid="preview-script-btn" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"><FileCode className="h-3.5 w-3.5" /> Preview</button>
                    <button onClick={copyCommands} disabled={!changedCount} data-testid="copy-script-btn" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"><Copy className="h-3.5 w-3.5" /> Copy</button>
                    <button onClick={exportScripts} disabled={!changedCount} data-testid="download-script-btn" className="flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold bg-[#00E5FF] text-black hover:bg-[#00B3CC] hover:scale-105 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"><Download className="h-4 w-4" /> Export</button>
                  </div>
                ) : completion ? (
                  <div className="flex items-center gap-4 flex-wrap justify-end" data-testid="completion-bar">
                    <div className="text-sm font-mono-ui text-[#34C759] flex items-center gap-3">
                      <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {completion.files} files renamed</span>
                      <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {completion.folders} folders renamed</span>
                    </div>
                    <button onClick={undoApply} data-testid="undo-changes-btn" className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200"><Undo2 className="h-4 w-4" /> Undo Changes</button>
                    <button onClick={() => setCompletion(null)} data-testid="completion-done-btn" className="rounded-full px-3 py-2 text-xs bg-white/5 text-neutral-300 border border-white/15 hover:bg-white/10 transition-all duration-200">Done</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="relative">
                      <button onClick={() => setShowAdvanced((v) => !v)} data-testid="advanced-menu-btn" className="flex items-center gap-1.5 rounded-full px-3 py-2 text-xs bg-white/5 text-neutral-300 border border-white/15 hover:bg-white/10 transition-all duration-200"><MoreVertical className="h-3.5 w-3.5" /> Advanced</button>
                      {showAdvanced && <div className="fixed inset-0 z-20" onClick={() => setShowAdvanced(false)} data-testid="advanced-backdrop" />}
                      {showAdvanced && (
                        <div className="absolute bottom-full right-0 mb-2 w-52 rounded-xl bg-[#111] border border-white/15 shadow-2xl p-1.5 z-30" data-testid="advanced-menu">
                          <button onClick={() => { setShowAdvanced(false); resetSelected(); }} data-testid="adv-reset-selected" className="w-full text-left px-3 py-2 text-xs text-neutral-300 rounded-lg hover:bg-white/10">Reset selected</button>
                          <button onClick={() => { setShowAdvanced(false); resetAll(); }} data-testid="adv-reset-all" className="w-full text-left px-3 py-2 text-xs text-neutral-300 rounded-lg hover:bg-white/10">Reset all</button>
                          <div className="h-px bg-white/10 my-1" />
                          <button onClick={() => { setShowAdvanced(false); setShowScript(true); }} disabled={!changedCount} data-testid="adv-preview" className="w-full text-left px-3 py-2 text-xs text-neutral-300 rounded-lg hover:bg-white/10 disabled:opacity-40">Preview script</button>
                          <button onClick={() => { setShowAdvanced(false); exportScripts(); }} disabled={!changedCount} data-testid="adv-export" className="w-full text-left px-3 py-2 text-xs text-neutral-300 rounded-lg hover:bg-white/10 disabled:opacity-40">Export rename.sh</button>
                          <button onClick={() => { setShowAdvanced(false); copyCommands(); }} disabled={!changedCount} data-testid="adv-copy" className="w-full text-left px-3 py-2 text-xs text-neutral-300 rounded-lg hover:bg-white/10 disabled:opacity-40">Copy commands</button>
                        </div>
                      )}
                    </div>
                    {collision.hasConflicts ? (
                      <button onClick={() => setFilterKind("conflicts")} data-testid="resolve-conflicts-btn" className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF3B30] text-white hover:brightness-110 transition-all duration-200"><ShieldCheck className="h-4 w-4" /> Resolve {summary.conflicts} Conflict{summary.conflicts !== 1 ? "s" : ""}</button>
                    ) : (
                      <button onClick={requestApply} disabled={!changedCount} data-testid="apply-renames-btn" className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold bg-[#00E5FF] text-black hover:bg-[#00B3CC] hover:scale-105 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                        <Play className="h-4 w-4" /> {changedCount ? `Apply ${changedCount} Rename${changedCount !== 1 ? "s" : ""}` : "No Changes"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Paste modal */}
      {showPaste && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl bg-[#0A0A0A] border border-white/15 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10"><h3 className="font-display font-bold text-white">Paste file / folder names</h3><button onClick={() => setShowPaste(false)} data-testid="paste-close-btn" className="text-neutral-500 hover:text-white transition-colors duration-200"><X className="h-5 w-5" /></button></div>
            <div className="p-5">
              <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} data-testid="paste-textarea" rows={8} placeholder={"MyFolder/\nMyFolder/32735_Lolita_Taylor_watermarked.mp4\nMyFolder/sub album/\nMyFolder/sub album/889_Emma_Stone_HD.mp4"} className="w-full bg-black/60 border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white font-mono-ui outline-none focus:border-[#00E5FF]/50 resize-none" />
              <p className="text-xs text-neutral-500 mt-2 font-mono-ui">One per line. Use <span className="text-[#00E5FF]">/</span> for nesting; end with <span className="text-[#00E5FF]">/</span> to mark a folder.</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-white/10"><button onClick={() => setShowPaste(false)} data-testid="paste-cancel-btn" className="rounded-full px-4 py-2 text-sm bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200">Cancel</button><button onClick={applyPaste} data-testid="paste-load-btn" className="rounded-full px-5 py-2 text-sm font-semibold bg-[#00E5FF] text-black hover:bg-[#00B3CC] transition-all duration-200">Load names</button></div>
          </div>
        </div>
      )}

      {/* Script preview modal */}
      {showScript && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-[#0A0A0A] border border-white/15 shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <button onClick={() => setScriptTab("rename")} data-testid="script-tab-rename" className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full ${scriptTab === "rename" ? "bg-[#00E5FF] text-black" : "text-neutral-300"}`}><FileCode className="h-4 w-4" /> rename.sh</button>
                <button onClick={() => setScriptTab("undo")} data-testid="script-tab-undo" className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full ${scriptTab === "undo" ? "bg-[#00E5FF] text-black" : "text-neutral-300"}`}><Undo2 className="h-4 w-4" /> undo_rename.sh</button>
              </div>
              <button onClick={() => setShowScript(false)} data-testid="script-close-btn" className="text-neutral-500 hover:text-white transition-colors duration-200"><X className="h-5 w-5" /></button>
            </div>
            <pre data-testid="script-preview" className="flex-1 overflow-auto p-5 text-xs font-mono-ui text-neutral-300 whitespace-pre-wrap">{scriptTab === "rename" ? renameScript : undoScript}</pre>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-white/10"><button onClick={exportScripts} data-testid="script-export-btn" className="rounded-full px-5 py-2 text-sm font-semibold bg-[#00E5FF] text-black hover:bg-[#00B3CC] transition-all duration-200">Export both</button></div>
          </div>
        </div>
      )}

      {/* Confirm apply (Mac) */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-[#0A0A0A] border border-white/15 shadow-2xl overflow-hidden" data-testid="confirm-apply-modal">
            <div className="px-6 py-5">
              <h3 className="font-display font-black text-white text-xl mb-2">Apply {changedCount} change{changedCount !== 1 ? "s" : ""}?</h3>
              <p className="text-neutral-400 leading-relaxed">{summary.files} file{summary.files !== 1 ? "s" : ""} and {summary.folders} folder{summary.folders !== 1 ? "s" : ""} will be renamed. Nothing will be deleted.</p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/10">
              <button onClick={() => setShowConfirm(false)} data-testid="confirm-cancel-btn" className="rounded-full px-4 py-2 text-sm bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all duration-200">Cancel</button>
              <button onClick={confirmApply} data-testid="confirm-apply-btn" className="rounded-full px-5 py-2 text-sm font-semibold bg-[#00E5FF] text-black hover:bg-[#00B3CC] transition-all duration-200">Apply Renames</button>
            </div>
          </div>
        </div>
      )}

      {/* Applying overlay */}
      {applying && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="applying-overlay">
          <div className="rounded-2xl bg-[#0A0A0A] border border-white/15 px-8 py-6 flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 text-[#00E5FF] animate-spin" />
            <div className="text-white font-mono-ui text-sm">Renaming… {applying.done}/{applying.total}</div>
          </div>
        </div>
      )}

      {/* History drawer */}
      <AnimatePresence>
        {showHistory && (
          <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowHistory(false)} />
            <motion.div className="absolute right-0 top-0 h-full w-[90vw] max-w-md bg-[#0A0A0A] border-l border-white/15 flex flex-col" initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }} transition={{ type: "tween" }} data-testid="history-drawer">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10"><h3 className="font-display font-bold text-white flex items-center gap-2"><History className="h-4 w-4 text-[#00E5FF]" /> Rename History</h3><button onClick={() => setShowHistory(false)} className="text-neutral-500 hover:text-white"><X className="h-5 w-5" /></button></div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {sessions.length === 0 && <p className="text-sm text-neutral-500 font-mono-ui">No sessions yet. Export a rename to start your history.</p>}
                {sessions.map((s) => (
                  <div key={s.id} className="rounded-lg border border-white/10 bg-black/40 px-4 py-3" data-testid={`history-item-${s.id}`}>
                    <div className="flex items-center justify-between"><span className="text-sm text-white font-mono-ui truncate">{s.folder}</span><span className="text-[10px] text-neutral-500">{new Date(s.date).toLocaleString()}</span></div>
                    <div className="text-xs text-neutral-500 mt-1 font-mono-ui">{s.renamed} renamed · {s.files} files · {s.folders} folders · {s.mode}{s.undo ? " · undo available" : ""}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
